import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

import md5 from 'crypto-js/md5';
import FirebaseBroadcaster from '../firebaseBroadcaster';
import {
    organizerInsertIndex,
    nextStageDate,
    buildStageInsert,
    isOrganizerStage,
    copyStageStart,
} from '../lib/conventionKanban';

configure({ enforceActions: 'always' });

// The convention schema defines participants1..participants100, and a Lag's shooters
// live in the slot matching the Lag's position — so 100 Lag is the hard ceiling.
const MAX_STAGES = 100;

class ConventionStore extends LocalModel {
    constructor() {
        super('convention', {
            namePlural: 'conventions',
            namePluralReal: 'conventions',
            sort: '-startDate',
            limit: 100,
            api: {
                search: {
                    url: '/api/conventions/',
                    params: {
                        limit: 15,
                        sort: '-startDate',
                    },
                },
                load: {
                    url: '/api/conventions/',
                    params: {},
                },
                save: {
                    url: '/api/conventions/',
                    params: {},
                },
                delete: {
                    url: '/api/conventions/',
                    params: {},
                },
            },
        });
        // Real-time broadcast plumbing (lazy: avoid touching Firebase at module load).
        this.broadcaster = null;
        this.broadcastSenderId = `${Math.random().toString(36).substring(2)}${Date.now().toString(36)}`;
    }

    @observable reloadTimer = null;

    // Session-persisted UI state for the convention list, so the chosen tab
    // (Kommende/Ferdige) and the typed search survive navigating into a
    // convention and back (the list route unmounts on navigation).
    @observable listTab = null;

    @observable listSearch = '';

    @observable newConvention = {};

    @observable convention = {};

    @observable conventions = [];

    @observable participants = [];

    @observable tags = [];

    @observable jwtToken = null;

    @observable otp = '';

    @observable summary = {};

    @observable disciplineMapLoaded = 0;

    @observable disciplineMap = [
        // ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
        {
            key: 'fin',
            id: "8058ce1c-fd4c-41d1-ad35-72db3f9b6c2e",
            name: "Finfelt",
            disciplineCode: "5",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302516,
            niF_Id: 300316,
        },
        {
            key: 'grov',
            id: "13062da9-6280-41ab-9dd5-e7bf3aa00704",
            name: "Grovfelt",
            disciplineCode: "11",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302516,
            niF_Id: 300317,
            deleted: false,
        },
        {
            key: 'mil',
            id: "5e666bb9-e59c-4dbd-96e3-c3a825ed8f87",
            name: "Militærfelt",
            disciplineCode: "8",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302516,
            niF_Id: 300319,
            deleted: false,
        },
        {
            key: 'rev',
            id: "3a3cc313-9575-4863-b0f3-ca1c3c66336e",
            name: "Revolverfelt",
            disciplineCode: "9",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302516,
            niF_Id: 300318,
            deleted: false,
        },
        {
            key: 'sp.rev',
            id: "13ebbe4a-034e-40d2-8008-066c30883212",
            name: "Spesialrevolver",
            disciplineCode: "71",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302518,
            niF_Id: 300320,
            deleted: false,
        },
        {
            key: 'sp.pistol',
            id: "5328e2ab-a720-40b9-8d8d-9e594bed3e6a",
            name: "Spesialpistol",
            disciplineCode: "73",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302518,
            niF_Id: 302644,
            deleted: false,
        },
        {
            key: 'mag1',
            id: "09430a6b-872e-45e8-aabc-8a86fd0d51c8",
            name: "Magnumfelt 1",
            disciplineCode: "72",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302518,
            niF_Id: 302645,
        },
        {
            key: 'mag2',
            id: "532764a5-879c-4e61-bd3c-e235277b2c05",
            name: "Magnumfelt 2",
            disciplineCode: "74",
            branchId: "0beff7e2-94f7-4284-b76e-fc3e17b29e4f",
            niF_ParentId: 302518,
            niF_Id: 302646,
        },
    ];

    @observable classMapLoaded = 0;

    @observable classMap = [];

    // A convention that does not exist, or is not accessible for the current
    // hostname/user (a deleted id — `offline: 1` — or a customer scope mismatch),
    // comes back from the API as an empty `data` array. preact-storemodel's
    // handleApiLoadResponse then calls `updateItem(response.data[0])`, i.e.
    // `updateItem(undefined)`, which would park `undefined` in this observable
    // instead of its `{}` default. Every component that reads
    // `conventionStore.convention` directly then throws on its first property
    // access (the /convention/330 crashes on `sponsors` and `autoplayVideo`).
    // Coerce a missing item back to an empty object at the source so no reader
    // can ever see `undefined`. `super.updateItem` is the base class `@action`,
    // so the assignment still happens inside an action.
    updateItem(item) {
        super.updateItem(item || {});
    }

    @action
    setReloadTimer = (timer) => {
        if (this.reloadTimer) {
            clearTimeout(this.reloadTimer);
        }
        this.reloadTimer = timer;
    }

    broadcast = (command, payload = {}) => {
        try {
            if (!this.broadcaster) {
                this.broadcaster = new FirebaseBroadcaster();
            }
            const p = this.broadcaster.sendCommand(command, {
                ...payload,
                convention: this.convention?.id,
                senderId: this.broadcastSenderId,
            });
            if (p && typeof p.catch === 'function') {
                p.catch(err => console.error('[convention] broadcast send failed:', err));
            }
        } catch (err) {
            console.error('[convention] broadcast failed:', err);
        }
    };

    @action
    deleteReloadTimer = () => {
        clearTimeout(this.reloadTimer);
        this.reloadTimer = null;
    }

    async getJwt(convention) {
        const response = await util.fetchApi(`/api/conventions/jwt/${convention}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('jwtToken', response.data.jwtToken);
                this.updateKeyValue('otp', response.data.otp);
                return response;
            default:
                return 'Error';
        }
    }

    async checkOtp(convention, otp) {
        const response = await util.fetchApi(`/api/conventions/otp/${convention}/${otp}`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                return response;
            default:
                return 'Error';
        }
    }

    // `extra` carries IPSC signup fields (powerFactor, ipscCategory) — see lib/ipscParticipant.js.
    async signUp(user, shootingClass, extra = {}) {
        const response = await util.fetchApi(`/api/conventions/signup/${this.convention.id}`, { method: 'PATCH' }, {
            id: this.convention.id,
            participants: {
                id: user.id,
                firstname: (user.firstname || '').trim(),
                lastname: (user.lastname || '').trim(),
                cellphone: user.cellphone,
                email: user.email,
                class: shootingClass,
                md5: md5(JSON.stringify(user)).toString(),
                signupDate: new Date(),
                ...(extra && typeof extra === 'object' ? extra : {}),
            },
        });
        if (response && response.status >= 200 && response.status < 300) {
            this.broadcast('participantAdded', { stageMd5: null, participantId: user.id });
        }
        return response;
    }

    async signUpAsAdmin(user) {
        // console.log('signUp', user);
        const response = await this.save({
            id: this.convention.id,
            participants: {
                id: user.id,
                firstname: (user.firstname || '').trim(),
                lastname: (user.lastname || '').trim(),
                cellphone: user.cellphone,
                email: user.email,
                md5: md5(JSON.stringify(user)).toString(),
            },
        });
        if (response && response.status >= 200 && response.status < 300) {
            this.broadcast('participantAdded', { stageMd5: null, participantId: user.id });
        }
    }

    async newRound(type) {
        if (!type) {
            return;
        }
        // const participants = [...this.convention.participants];
        // Next in line after the previous MAIN Lag. Arrangørlag lead the list and
        // carry the day-before date, so chaining off one would date this Lag a day
        // early — nextStageDate() skips them and falls back to the convention start.
        const nextDate = nextStageDate({
            stages: this.convention.stages,
            startDate: this.convention.startDate,
            stageInterval: this.convention.stageInterval,
        }) || new Date();

        const stageNumber = this.convention.stages.length + 1;

        let stagesValue = null;
        if (type === 'norgesfelt') {
            stagesValue = 8;
        } else if (type === 't-96') {
            stagesValue = 16;
        } else if (type === 'fri') {
            stagesValue = 6;
        }
        const newStage = {
            type,
            stages: stagesValue,
            name: `Lag ${stageNumber}`,
            date: nextDate,
            idx: stageNumber,
            stageSlots: this.convention.stageSlots,
            stageClosed: 1,
        };
        newStage.md5 = md5(JSON.stringify(this.convention.stages)).toString();

        // Optimistically append the new stage to the local observable so the admin
        // list updates immediately. save() only PATCHes the backend and does not touch
        // the local observable, and the caller's loadAll() reload is a slow multi-request
        // round-trip; without this the list would not refresh until that completes.
        // loadAll() later reconciles with the authoritative server state.
        this.updateObjectKeyValue('convention', 'stages', [...this.convention.stages, newStage]);

        const response = await this.save({
            id: this.convention.id,
            // [`participants${stageNumber}`]: participants,
            stages: newStage,
        });

        return response;
    }

    /**
     * Create an arrangorlag - a Lag for the arranging club's own shooters, held the
     * DAY BEFORE the main competition.
     *
     * It shares the single "Lag 1..x" numbering with every other Lag, so to keep that
     * numbering chronological the new Lag is INSERTED at the front (after any existing
     * arrangorlag) instead of appended. Participants are positional
     * (stages[i] maps to participants{i+1}), so the insert has to shift every slot from
     * the insert point up by one and re-stamp name/idx on all stages - the mirror image
     * of the shift deleteStage() performs.
     *
     * @param {string} type - Stage type (felt, norgesfelt, ...), as for newRound()
     * @returns {Object} - API response
     */
    async newOrganizerRound(type) {
        if (!type) {
            return { status: 400, errorCode: 'MISSING_TYPE', message: 'Mangler lagtype' };
        }
        const stages = this.convention.stages || [];
        if (stages.length >= MAX_STAGES) {
            return {
                status: 400,
                errorCode: 'TOO_MANY_STAGES',
                message: `Et stevne kan ha maks ${MAX_STAGES} lag`,
            };
        }

        const insertIndex = organizerInsertIndex(stages);
        const nextDate = nextStageDate({
            stages,
            startDate: this.convention.startDate,
            stageInterval: this.convention.stageInterval,
            organizer: true,
        }) || new Date();

        let stagesValue = null;
        if (type === 'norgesfelt') {
            stagesValue = 8;
        } else if (type === 't-96') {
            stagesValue = 16;
        } else if (type === 'fri') {
            stagesValue = 6;
        }
        const newStage = {
            type,
            stages: stagesValue,
            name: `Lag ${insertIndex + 1}`,
            date: nextDate,
            idx: insertIndex + 1,
            stageSlots: this.convention.stageSlots,
            stageClosed: 1,
            organizerStage: 1,
        };
        // Timestamped so two arrangorlag created back to back can never collide.
        newStage.md5 = md5(JSON.stringify({ ...newStage, timestamp: Date.now(), insertIndex })).toString();

        return this.insertStageAt({ insertIndex, newStage });
    }

    /**
     * Apply a planned INSERT into the positional stages array: renumber every Lag,
     * shift the participant slots the insert disturbs, seed the new Lag's slot,
     * update the local observable optimistically and PATCH the convention.
     *
     * Shared by newOrganizerRound() (empty new Lag) and copyOrganizerStage()
     * (new Lag pre-filled with the copied shooters).
     *
     * @param {number} insertIndex - 0-based position the new Lag takes in stages
     * @param {Object} newStage - the Lag to insert (name/idx are re-stamped here)
     * @param {Array} participants - shooters for the new Lag's slot, default empty.
     *                               Snapshot it BEFORE calling: this method rewrites
     *                               the convention observable.
     * @returns {Object} - API response
     */
    async insertStageAt({ insertIndex, newStage, participants = [] }) {
        const stages = this.convention.stages || [];
        const { stages: newStages, participantMoves } = buildStageInsert(stages, insertIndex, newStage);

        // New value for every participant slot the insert disturbs. Moves come
        // highest-slot-first, and each reads the CURRENT convention, so no slot is
        // read after it has been written.
        const participantSlots = {};
        participantMoves.forEach(({ from, to }) => {
            participantSlots[to] = this.convention[`participants${from}`] || [];
        });
        // The slot the new Lag now occupies: empty for a fresh Lag, the copied
        // shooters for a copy. Moves only ever target slots above this one, so this
        // assignment can never clobber a shifted slot.
        participantSlots[insertIndex + 1] = participants;

        const body = {
            id: this.convention.id,
            'stages-overwrite': newStages,
        };
        Object.keys(participantSlots).forEach(slot => {
            body[`participants${slot}-overwrite`] = participantSlots[slot];
        });

        // Optimistic local update so the kanban renumbers immediately - the PATCH
        // never touches the local observable and the caller's reload() is a slow
        // multi-request round-trip. reload() later reconciles with the server.
        const localPatch = { stages: newStages };
        Object.keys(participantSlots).forEach(slot => {
            localPatch[`participants${slot}`] = participantSlots[slot];
        });
        this.updateKeyValue('convention', { ...this.convention, ...localPatch });

        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, body);
        return response;
    }

    /**
     * Copy an arrangorlag - the Lag itself AND every shooter in it - into a new
     * arrangorlag starting ORGANIZER_COPY_HOURS (2) hours later: same type, same
     * station count, same slot count, same shooters in the same order.
     *
     * The copy is inserted at the end of the leading arrangorlag block, exactly like
     * a brand new arrangorlag, so the single "Lag 1..x" numbering stays continuous.
     * It always starts as "Venter" (stageClosed), never as started or done.
     *
     * @param {string} stageMd5 - md5 of the arrangorlag to copy
     * @returns {Object} - API response
     */
    async copyOrganizerStage(stageMd5) {
        const stages = this.convention.stages || [];
        const sourceIdx = stages.findIndex(s => s.md5 === stageMd5);
        if (sourceIdx < 0) {
            return { status: 404, errorCode: 'STAGE_NOT_FOUND', message: 'Laget finnes ikke' };
        }
        const source = stages[sourceIdx];
        if (!isOrganizerStage(source)) {
            return { status: 400, errorCode: 'NOT_ORGANIZER_STAGE', message: 'Bare arrangørlag kan kopieres' };
        }
        if (stages.length >= MAX_STAGES) {
            return {
                status: 400,
                errorCode: 'TOO_MANY_STAGES',
                message: `Et stevne kan ha maks ${MAX_STAGES} lag`,
            };
        }
        const nextDate = copyStageStart(source.date);
        if (!nextDate) {
            return { status: 400, errorCode: 'MISSING_STAGE_DATE', message: 'Laget mangler starttidspunkt' };
        }

        const insertIndex = organizerInsertIndex(stages);
        const newStage = {
            type: source.type,
            stages: source.stages,
            name: `Lag ${insertIndex + 1}`,
            date: nextDate,
            idx: insertIndex + 1,
            stageSlots: source.stageSlots,
            stageClosed: 1,
            organizerStage: 1,
        };
        // Timestamped so two copies made back to back can never collide.
        newStage.md5 = md5(JSON.stringify({ ...newStage, timestamp: Date.now(), insertIndex })).toString();

        // Snapshot the source shooters BEFORE the insert shifts any slot, as plain
        // objects in a plain array so the copy shares neither the array nor a single
        // participant object with the Lag it was copied from.
        const sourceParticipants = (this.convention[`participants${sourceIdx + 1}`] || [])
            .map(participant => ({ ...participant }));

        return this.insertStageAt({ insertIndex, newStage, participants: sourceParticipants });
    }

    // `extra` = further fields stamped on the Lag entry (e.g. { standplass: 4 } so a
    // shooter lands on a specific firing point — see lib/conventionKanban.js).
    async newParticipant(user = {}, stageMd5 = null, isAdmin = false, shootingClass = null, skillClass = null, extra = {}) {
        if (!user.id) {
            return { status: 400, errorCode: 'MISSING_USER', message: 'User ID is required' };
        }

        // For non-admin stage signups, use the atomic endpoint to prevent race conditions
        if (!isAdmin && stageMd5 && shootingClass) {
            const response = await util.fetchApi(
                `/api/conventions/${this.convention.id}/stage-signup`,
                { method: 'POST' },
                { stageMd5, shootingClass }
            );
            if (response && response.status >= 200 && response.status < 300) {
                this.broadcast('participantAdded', { stageMd5, participantId: user.id });
            }
            return response;
        }

        // Admin flow or non-stage signup: use the original save method
        let stageName = 'participants';
        let currentStage;
        if (stageMd5) {
            const stageIdx = this.convention.stages.findIndex((s) => s.md5 === stageMd5);
            if (stageIdx >= 0) {
                stageName = `participants${stageIdx + 1}`;
                currentStage = this.convention.stages[stageIdx];
            }
        }

        // The participantsN array doesn't exist on the convention until the first
        // shooter is added to that Lag, so default to [] before reading .length/.find
        // (otherwise adding to an empty/high Lag throws "can't access property length").
        const stageParticipants = this.convention[stageName] || [];

        // Capacity check (for admin signups or non-stage signups)
        if (currentStage && currentStage.stageSlots > 0) {
            const totalParticipants = stageParticipants.length;
            if (totalParticipants >= currentStage.stageSlots) {
                return { status: 409, errorCode: 'STAGE_FULL', message: 'Beklager, runden er nå full' };
            }
        }

        // Check if participant already exists
        const existingParticipant = stageParticipants.find((p) => p.id === user.id);
        if (existingParticipant) {
            return { status: 409, errorCode: 'ALREADY_SIGNED_UP', message: 'Deltakeren er allerede påmeldt' };
        }

        const savePayload = {
            id: this.convention.id,
            [stageName]: {
                id: user.id,
                firstname: (user.firstname || '').trim(),
                lastname: (user.lastname || '').trim(),
                cellphone: user.cellphone,
                email: user.email,
                sportsClub: user.sportsClub || '',
                nsfClubNumber: user.nsfClubNumber || '',
                nsfId: user.nsfId || 0,
                class: shootingClass,
                skillClass,
                md5: md5(JSON.stringify(user)).toString(),
                date: util.isoDate(),
                signupDate: new Date(),
                ...(stageName !== 'participants' && extra && typeof extra === 'object' ? extra : {}),
            },
        };

        // Assigning someone to a Lag (stage) must also register them for the
        // convention itself. The backend dedupes participants* arrays by user id,
        // so including an already-signed-up user here is a no-op.
        if (stageName !== 'participants') {
            savePayload.participants = {
                id: user.id,
                firstname: (user.firstname || '').trim(),
                lastname: (user.lastname || '').trim(),
                cellphone: user.cellphone,
                email: user.email,
                sportsClub: user.sportsClub || '',
                nsfClubNumber: user.nsfClubNumber || '',
                nsfId: user.nsfId || 0,
                class: shootingClass,
                skillClass,
                md5: md5(JSON.stringify(user)).toString(),
                signupDate: new Date(),
            };
        }

        const response = await this.save(savePayload);
        if (response && response.status >= 200 && response.status < 300) {
            this.broadcast('participantAdded', { stageMd5: stageMd5 || null, participantId: user.id });
        }
        return response;
    }

    async createParticipantUser(user = {}) {
        if (!this.convention?.id) {
            return { status: 400, errorCode: 'MISSING_CONVENTION', message: 'Convention ID is required' };
        }
        return util.fetchApi(
            `/api/conventions/${this.convention.id}/users`,
            { method: 'POST' },
            user,
        );
    }

    /**
     * Remove a participant from a stage (resign)
     * @param {number} userId - The user ID to remove
     * @param {string} stageMd5 - The stage MD5 identifier
     * @returns {Object} - API response
     */
    async removeParticipantFromStage(userId, stageMd5) {
        if (!userId || !stageMd5) {
            throw new Error('Ugyldig bruker eller runde');
        }

        const stages = this.convention.stages || [];
        const stageIdx = stages.findIndex(s => s.md5 === stageMd5);
        if (stageIdx < 0) {
            throw new Error('Runden finnes ikke');
        }

        const stageNumber = stageIdx + 1;
        const participantsFieldName = `participants${stageNumber}`;
        const participants = this.convention[participantsFieldName] || [];

        // Find the participant to get their md5
        const participant = participants.find(p => p.id === userId);
        if (!participant) {
            throw new Error('Du er ikke påmeldt denne runden');
        }

        // Make direct API call to remove the participant
        const removeFieldName = `remove${util.ucfirst(participantsFieldName)}ByMd5`;
        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, {
            [removeFieldName]: participant.md5,
        });

        if (response && response.status >= 200 && response.status < 300) {
            this.broadcast('participantRemoved', { stageMd5, participantId: userId });
        }
        return response;
    }

    /**
     * Delete a stage (Lag) from the convention.
     *
     * Participants are stored positionally: stages[i] maps to participants{i+1},
     * and the results report reads them by stage.idx. Removing a stage therefore
     * requires three coordinated changes so both lookups stay aligned:
     *   1. shift every participant slot after the deleted one down by one,
     *   2. clear the now-unused trailing slot,
     *   3. renumber the remaining stages' name and idx to match their new positions,
     *      so the Lag numbering stays a single unbroken 1..x series.
     *
     * @param {string} stageMd5 - The stage MD5 identifier
     * @returns {Object} - API response
     */
    async deleteStage(stageMd5) {
        const stages = this.convention.stages || [];
        const delIdx = stages.findIndex(s => s.md5 === stageMd5);
        if (delIdx < 0) {
            return { status: 404, errorCode: 'STAGE_NOT_FOUND', message: 'Runden finnes ikke' };
        }

        const total = stages.length;
        const newStages = stages
            .filter((s, i) => i !== delIdx)
            .map((s, i) => ({ ...s, name: `Lag ${i + 1}`, idx: i + 1 }));

        const body = {
            id: this.convention.id,
            'stages-overwrite': newStages,
        };
        // Shift participants{delIdx+2..total} down into participants{delIdx+1..total-1}.
        for (let pos = delIdx + 1; pos < total; pos += 1) {
            body[`participants${pos}-overwrite`] = this.convention[`participants${pos + 1}`] || [];
        }
        // The highest slot no longer maps to a stage — clear it.
        body[`participants${total}-overwrite`] = [];

        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, body);
        return response;
    }

    /**
     * Reorder participants within a single Lag (stage). Order is positional in the
     * participants{n} array, so we overwrite the whole array. Mirrors the
     * `participants{n}-overwrite` mechanism used by deleteStage.
     * @param {string} stageMd5 - The stage MD5 identifier
     * @param {Array} orderedParticipants - The full participant array in the new order
     * @returns {Object} - API response
     */
    async reorderStageParticipants(stageMd5, orderedParticipants) {
        if (!Array.isArray(orderedParticipants)) {
            throw new Error('Ugyldig deltakerliste');
        }
        const stages = this.convention.stages || [];
        const stageIdx = stages.findIndex(s => s.md5 === stageMd5);
        if (stageIdx < 0) {
            throw new Error('Runden finnes ikke');
        }
        const field = `participants${stageIdx + 1}`;
        // Optimistic local update so the column re-renders in the new order immediately,
        // restoring the previous order if the PATCH fails so local state can't diverge
        // from the server even when the caller doesn't reload.
        const previous = this.convention[field];
        this.updateObjectKeyValue('convention', field, orderedParticipants);
        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, {
            [`${field}-overwrite`]: orderedParticipants,
        });
        if (!response || response.status >= 400) {
            this.updateObjectKeyValue('convention', field, previous);
        }
        return response;
    }

    /**
     * Toggle omskyting on/off for one result section (weapon/age class).
     * Optimistic local update with rollback, same pattern as reorderParticipants.
     */
    @action
    async toggleShootOffSection(sectionKey) {
        const previous = this.convention.shootOffSections || [];
        const next = previous.includes(sectionKey)
            ? previous.filter(key => key !== sectionKey)
            : [...previous, sectionKey];
        this.updateObjectKeyValue('convention', 'shootOffSections', next);
        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, {
            'shootOffSections-overwrite': next,
        });
        if (!response || response.status >= 400) {
            this.updateObjectKeyValue('convention', 'shootOffSections', previous);
        }
        return response;
    }

    @action
    async loadDisciplineMap() {
        const now = new Date().getTime();
        if (now - this.disciplineMapLoaded < 1000 * 60 * 60 * 1) { // Reload every 1 hour
            return;
        }
        this.updateKeyValue('disciplineMapLoaded', now);

        const response = await util.fetchApi(`/api/integrations/rubic/disciplines`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                // console.log('rubicGetDisciplines', response);
            default:
                // // console.log('rubicGetDisciplines', response);
        }
        if (response.data) {
            // Merge with existing disciplineMap
            const newMap = this.disciplineMap.map((d) => {
                const found = response.data.find((r) => r.id === d.id);
                if (found) {
                    return { ...d, ...found };
                }
                return d;
            });
            // Add new disciplines
            response.data.forEach((d) => {
                if (!newMap.find((existing) => existing.id === d.id)) {
                    newMap.push(d);
                }
            });
            this.updateKeyValue('disciplineMap', newMap);
        }
    }

    @action
    async loadClassMap() {
        const now = new Date().getTime();
        if (now - this.classMapLoaded < 1000 * 60 * 60 * 1) { // Reload every 1 hour
            return;
        }
        this.updateKeyValue('classMapLoaded', now);

        const response = await util.fetchApi(`/api/integrations/rubic/classes`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                // console.log('rubicGetClasses', response);
            default:
                // // console.log('rubicGetClasses', response);
        }
        if (response.data) {
            this.updateKeyValue('classMap', response.data);
        }
    }

    getNsfFallbackClass() {
        const fallbackClass = this.classMap.find(c => c.id === '32c29bc5-d0be-4639-a70f-1b17e886e3cd');
        return fallbackClass;
    }

    calculateStagePreview({ startTime, endTime, intervalMinutes }) {
        if (!startTime || !endTime || !intervalMinutes || intervalMinutes <= 0) {
            return 0;
        }
        const start = new Date(startTime);
        const end = new Date(endTime);
        if (end <= start) {
            return 0;
        }
        const diffMs = end.getTime() - start.getTime();
        const diffMinutes = diffMs / (1000 * 60);
        return Math.floor(diffMinutes / intervalMinutes) + 1;
    }

    /**
     * Check if a user is registered for the convention (in main participants array)
     * @param {number} userId - The user ID to check
     * @returns {boolean} - True if user is registered for the convention
     */
    isUserRegisteredForConvention(userId) {
        const participants = this.convention.participants || [];
        return participants.some(p => p.id === userId);
    }

    /**
     * Get all stages a user is signed up for
     * @param {number} userId - The user ID to check
     * @returns {Array} - Array of stages the user is signed up for
     */
    getUserSignedUpStages(userId) {
        const stages = this.convention.stages || [];
        const signedUpStages = [];

        stages.forEach((stage, idx) => {
            const stageNumber = idx + 1;
            const participantsFieldName = `participants${stageNumber}`;
            const participants = this.convention[participantsFieldName] || [];
            const userParticipant = participants.find(p => p.id === userId);
            if (userParticipant) {
                signedUpStages.push({
                    ...stage,
                    stageNumber,
                    participantsFieldName,
                    userClass: userParticipant.class || null,
                });
            }
        });

        return signedUpStages;
    }

    /**
     * Get all weapon classes a user has already used in this convention
     * @param {number} userId - The user ID to check
     * @returns {Array} - Array of class strings the user has used
     */
    getUserUsedClasses(userId) {
        const signedUpStages = this.getUserSignedUpStages(userId);
        const usedClasses = signedUpStages
            .map(s => s.userClass)
            .filter(c => c); // Filter out null/undefined
        return usedClasses;
    }

    /**
     * Check if a user can sign up for a specific stage
     * @param {number} userId - The user ID to check
     * @param {string} stageMd5 - The stage MD5 identifier
     * @returns {Object} - { allowed: boolean, reason: string }
     */
    canUserSignUpForStage(userId, stageMd5) {
        const stages = this.convention.stages || [];
        const stageIdx = stages.findIndex(s => s.md5 === stageMd5);

        if (stageIdx < 0) {
            return { allowed: false, reason: 'Runden finnes ikke' };
        }

        const targetStage = stages[stageIdx];
        const stageNumber = stageIdx + 1;
        const participantsFieldName = `participants${stageNumber}`;
        const participants = this.convention[participantsFieldName] || [];

        // Check if stage has free slots
        if (targetStage.stageSlots > 0 && participants.length >= targetStage.stageSlots) {
            return { allowed: false, reason: 'Runden er full' };
        }

        // Check if user is already signed up for this stage
        const isAlreadySignedUp = participants.some(p => p.id === userId);
        if (isAlreadySignedUp) {
            return { allowed: false, reason: 'Du er allerede påmeldt denne runden' };
        }

        // Check stage-gap rule (configurable via convention.stageGapHours, defaults to 2)
        const gapHours = this.convention.stageGapHours > 0 ? this.convention.stageGapHours : 2;
        const userStages = this.getUserSignedUpStages(userId);
        const targetStageTime = new Date(targetStage.date);

        for (const existingStage of userStages) {
            const existingTime = new Date(existingStage.date);
            const hoursDiff = Math.abs(targetStageTime - existingTime) / (1000 * 60 * 60);
            if (hoursDiff < gapHours) {
                const existingTimeStr = existingTime.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
                return {
                    allowed: false,
                    reason: `For nær ${existingStage.name} kl ${existingTimeStr}`,
                };
            }
        }

        return { allowed: true, reason: '' };
    }

    /**
     * Get all stages with availability info for a user
     * @param {number} userId - The user ID to check
     * @returns {Array} - Array of stages with availability info
     */
    getAvailableStagesForUser(userId) {
        const stages = this.convention.stages || [];

        return stages.map((stage, idx) => {
            const stageNumber = idx + 1;
            const participantsFieldName = `participants${stageNumber}`;
            const participants = this.convention[participantsFieldName] || [];
            const slotsUsed = participants.length;
            const slotsTotal = stage.stageSlots || 0;
            const userParticipant = participants.find(p => p.id === userId);
            const isUserSignedUp = !!userParticipant;
            const userClass = userParticipant?.class || null;

            const { allowed, reason } = this.canUserSignUpForStage(userId, stage.md5);

            return {
                ...stage,
                stageNumber,
                participantsFieldName,
                slotsUsed,
                slotsTotal,
                isUserSignedUp,
                userClass,
                canSignUp: allowed,
                blockedReason: reason,
            };
        });
    }

    async bulkGenerateStages({ type, startTime, endTime, intervalMinutes }) {
        // Validate inputs
        if (!type) {
            throw new Error('Stage type is required');
        }
        if (!startTime || !endTime) {
            throw new Error('Start time and end time are required');
        }
        if (!intervalMinutes || intervalMinutes <= 0) {
            throw new Error('Interval must be greater than 0');
        }

        const start = new Date(startTime);
        const end = new Date(endTime);
        if (end <= start) {
            throw new Error('End time must be after start time');
        }

        // Get existing stages
        const existingStages = this.convention.stages || [];
        const startingStageNumber = existingStages.length + 1;

        // Generate new stages
        const newStages = [];
        let currentTime = new Date(start);
        let stageIndex = 0;

        while (currentTime <= end) {
            const stageNumber = startingStageNumber + stageIndex;
            let stagesValue = null;
            if (type === 'norgesfelt') {
                stagesValue = 8;
            } else if (type === 't-96') {
                stagesValue = 16;
            }
            const newStage = {
                type,
                stages: stagesValue,
                name: `Lag ${stageNumber}`,
                date: new Date(currentTime),
                idx: stageNumber,
                stageSlots: this.convention.stageSlots,
                stageClosed: 1,
            };
            // Generate unique md5 using stage data and timestamp
            newStage.md5 = md5(JSON.stringify({ ...newStage, timestamp: Date.now(), index: stageIndex })).toString();

            newStages.push(newStage);

            // Move to next interval
            currentTime = new Date(currentTime.getTime() + intervalMinutes * 60 * 1000);
            stageIndex += 1;
        }

        // Combine existing stages with new stages
        const allStages = [...existingStages, ...newStages];

        // Save using stages-overwrite pattern
        const response = await this.save({
            id: this.convention.id,
            'stages-overwrite': allStages,
        });

        return response;
    }

    async addOrganizer(user, assignedFunction) {
        if (!user || !user.id) {
            return;
        }
        const existingOrganizers = this.convention.organizers || [];
        const alreadyAdded = existingOrganizers.find(o => o.id === user.id);
        if (alreadyAdded) {
            return { status: 409, message: 'Organizer already added' };
        }
        const response = await this.save({
            id: this.convention.id,
            organizers: {
                id: user.id,
                firstname: (user.firstname || '').trim(),
                lastname: (user.lastname || '').trim(),
                cellphone: user.cellphone,
                email: user.email,
                sportsClub: user.sportsClub || '',
                function: assignedFunction || '',
                md5: md5(JSON.stringify(user)).toString(),
                addedDate: new Date(),
            },
        });
        return response;
    }

    // Bulk-add organizers parsed from a pasted Excel/Google-Sheets list.
    // Entries are free-form (id: 0) — they don't need to be existing users.
    // Duplicates (by email or phone) against existing organizers are skipped.
    async addOrganizersFromList(rows) {
        if (!Array.isArray(rows) || rows.length === 0 || !this.convention?.id) {
            return { added: 0, skipped: 0 };
        }
        const existing = this.convention.organizers || [];
        const seenEmails = new Set(existing.filter(o => o.email).map(o => String(o.email).toLowerCase().trim()));
        const seenPhones = new Set(existing.filter(o => o.cellphone).map(o => String(o.cellphone).replace(/\s/g, '')));
        let added = 0;
        let skipped = 0;
        for (let i = 0; i < rows.length; i += 1) {
            const row = rows[i];
            const firstname = (row.firstname || '').trim();
            const lastname = (row.lastname || '').trim();
            const cellphone = (row.cellphone || '').trim();
            const email = (row.email || '').trim();
            const assignedFunction = (row.function || '').trim();
            if (!firstname && !lastname && !cellphone && !email) {
                skipped += 1;
                continue;
            }
            const emailKey = email.toLowerCase();
            const phoneKey = cellphone.replace(/\s/g, '');
            if ((emailKey && seenEmails.has(emailKey)) || (phoneKey && seenPhones.has(phoneKey))) {
                skipped += 1;
                continue;
            }
            // eslint-disable-next-line no-await-in-loop
            await this.save({
                id: this.convention.id,
                organizers: {
                    id: 0,
                    firstname,
                    lastname,
                    cellphone,
                    email,
                    function: assignedFunction,
                    md5: md5(`${firstname}|${lastname}|${cellphone}|${email}|${Date.now()}|${i}|${Math.random()}`).toString(),
                    addedDate: new Date(),
                },
            });
            if (emailKey) seenEmails.add(emailKey);
            if (phoneKey) seenPhones.add(phoneKey);
            added += 1;
        }
        return { added, skipped };
    }

    async removeOrganizer(organizerMd5) {
        if (!organizerMd5) {
            return;
        }
        const response = await util.fetchApi(`/api/conventions/${this.convention.id}`, { method: 'PATCH' }, {
            removeOrganizersByMd5: organizerMd5,
        });
        return response;
    }

    // Persist a new organizer order. Used when an admin sorts the list in the
    // admin drawer and wants that order to stick (organizers are added in the
    // order they happen to be entered, e.g. Stp 3 before Stp 1).
    async saveOrganizerOrder(organizers) {
        if (!this.convention?.id || !Array.isArray(organizers) || organizers.length === 0) {
            return;
        }
        const response = await this.save({
            id: this.convention.id,
            'organizers-overwrite': organizers,
        });
        return response;
    }

    async editOrganizerFunction(organizerMd5, newFunction) {
        if (!organizerMd5) {
            return;
        }
        const response = await this.editArrayObject({
            field: 'organizers',
            key: 'function',
            value: newFunction,
            id: this.convention.id,
            md5: organizerMd5,
            objectName: 'convention',
        });
        return response;
    }

    async initiateSignupPayment({ convention, user, stores, wantedPage }) {
        const { saleStore, vippsStore, productStore } = stores;

        // Load the product
        await productStore.load({
            query: { id: convention.signupProduct },
            url: '/api/products/public',
            addData: [],
        });
        const { products } = productStore;
        const product = products?.find(p => p.id === convention.signupProduct) || products?.[0];
        if (!product || !product.id) {
            throw new Error('Signup product not found');
        }

        // Create a quick sale
        const saleResult = await saleStore.createQuickSale({
            product,
            user,
            customer: product.customer,
            paymentMethod: 'vippsIntegration',
        });
        if (!saleResult || !saleResult.id) {
            throw new Error('Failed to create sale');
        }

        // Set payment method
        await saleStore.setPaymentMethod({
            id: saleResult.id,
            paymentMethod: 'vippsIntegration',
        });

        // Reload sale to get updated data
        const saleData = await saleStore.load(saleResult.id, false, {
            query: { allCustomers: true },
            skipUpdate: true,
        });
        const updatedSale = saleData?.[0] || saleResult;

        // Calculate amount
        const amount = saleStore.sumTotal(updatedSale.products, false);

        // Request Vipps payment
        const response = await vippsStore.requestPayment({
            currency: 'NOK',
            amount,
            title: `convention-signup-${convention.id}`,
            customer: product.customer,
            sale: updatedSale.id,
            convention: convention.id,
            conventionParticipantId: user.id,
            type: 'conventionSignup',
            wantedPage: wantedPage || '',
        });

        if (!response?.data?.redirectUrl) {
            throw new Error('No redirect URL returned from Vipps');
        }

        return {
            redirectUrl: response.data.redirectUrl,
            paymentUuidv4: response.data.paymentUuidv4,
            sale: updatedSale,
            amount,
        };
    }

    async bulkGenerateConventions({ sourceConventionId, conventions }) {
        // Validate inputs
        if (!sourceConventionId) {
            throw new Error('Source convention ID is required');
        }
        if (!conventions || !Array.isArray(conventions) || conventions.length === 0) {
            throw new Error('At least one convention date configuration is required');
        }

        const response = await util.fetchApi('/api/conventions/admin/bulk', { method: 'POST' }, {
            sourceConventionId,
            conventions,
        });

        return response;
    }

    /**
     * Preset table for the "simple create" wizard, straight from the backend
     * (backend/lib/convention-presets.js) so the webapp never carries a copy.
     */
    async loadWizardPresets() {
        const response = await util.fetchApi('/api/conventions/wizard/presets', { method: 'GET' }, {});
        if (response?.status === 200 && Array.isArray(response.data)) {
            return response.data;
        }
        return [];
    }

    /**
     * Create N conventions from one preset + a list of {startDate, endDate}.
     * Returns the raw response; callers check `status === 201`.
     */
    async wizardCreate({
        preset, title, location, signupProduct, dates, customer,
    }) {
        const response = await util.fetchApi('/api/conventions/wizard', { method: 'POST' }, {
            preset,
            title,
            location,
            signupProduct,
            dates,
            customer,
        });
        return response;
    }

    /**
     * Signup-fee candidates for the HOSTNAME club (not the caller's switched
     * user.customer): [{ id, name, priceIncVat, isMemberProduct }].
     */
    async loadWizardProducts({ customer }) {
        const response = await util.fetchApi('/api/conventions/wizard/products', { method: 'GET' }, { customer });
        if (response?.status === 200 && Array.isArray(response.data)) {
            return response.data;
        }
        return [];
    }

    /**
     * Quick-create a signup-fee product on the hostname club. Returns the raw
     * response; `response.data.product` is { id, name, priceIncVat } on 201.
     */
    async wizardCreateProduct({ customer, name, priceIncVat }) {
        const response = await util.fetchApi('/api/conventions/wizard/product', { method: 'POST' }, {
            customer,
            name,
            priceIncVat,
        });
        return response;
    }
}

const store = new ConventionStore();
export default store;
